/**
 * FormInput Component
 * 
 * A reusable form input component for text, date, email, number, and tel inputs.
 * Provides consistent styling, error handling, and accessibility across all forms.
 * 
 * Design Principles:
 * - Consistent padding and spacing
 * - Clear label and error message display
 * - Accessible with proper ARIA attributes
 * - Clean white background with navy blue accents
 */

import React from 'react';
import './FormInput.css';

export interface FormInputProps {
  /** Label text for the input */
  label: string;
  /** Input name attribute */
  name: string;
  /** Input type */
  type?: 'text' | 'date' | 'email' | 'number' | 'tel';
  /** Placeholder text */
  placeholder?: string;
  /** Input value */
  value: string;
  /** Change handler */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Key down handler */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Maximum character length */
  maxLength?: number;
  /** Additional CSS class name */
  className?: string;
  /** Error message to display */
  error?: string;
}

const FormInput: React.FC<FormInputProps> = ({
  label,
  name,
  type = 'text',
  placeholder,
  value,
  onChange,
  onKeyDown,
  required = false,
  disabled = false,
  maxLength,
  className = '',
  error,
}) => {
  return (
    <div className={`form-group form-input-group ${className}`}>
      <label htmlFor={name} className="form-group-label">
        {label}
        {required && <span className="required-asterisk">*</span>}
      </label>
      <div className="input-icon">
        <input
          id={name}
          type={type}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          required={required}
          disabled={disabled}
          maxLength={maxLength}
          className={`form-input ${error ? 'input-error' : ''}`}
          aria-invalid={!!error}
          aria-describedby={error ? `${name}-error` : undefined}
        />
      </div>
      {error && (
        <span id={`${name}-error`} className="error-message" role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

export default FormInput;

